(() => {
  'use strict';
  const KEY = 'fs.my-size.v1';
  const ORDER = ['xxs', 'xs', 's', 'm', 'l', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl'];
  const normalize = value => {
    const key = String(value ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    return ({ extrasmall: 'xs', xsmall: 'xs', small: 's', medium: 'm', large: 'l', extralarge: 'xl', xlarge: 'xl', xxlarge: '2xl', xxl: '2xl', extraextralarge: '2xl', xxxlarge: '3xl', xxxl: '3xl', extraextraextralarge: '3xl', xxsmall: 'xxs', extraextrasmall: 'xxs' })[key] || key;
  };
  const labelFor = value => {
    const key = normalize(value);
    return ORDER.includes(key) ? key.toUpperCase() : String(value ?? '').trim();
  };
  const dataFor = item => item?.sizeMeta || item?.product || item;
  const variantsFor = item => {
    const product = dataFor(item);
    if (!product || !Array.isArray(product.variants) || product.complete === false) return [];
    if (product.hasSize === false) return [];
    if (product.hasSize && (Number(product.rendered) !== Number(product.total) || product.variants.length !== Number(product.total))) return [];
    const index = (product.options || []).findIndex(option => /(^|\b)sizes?(\b|$)/i.test(typeof option === 'string' ? option : option?.name || ''));
    return product.variants.map(variant => {
      const size = variant.size ?? (index >= 0 ? (variant.options || [variant.option1, variant.option2, variant.option3])[index] : '');
      return { ...variant, size: String(size ?? ''), normalizedSize: normalize(size) };
    }).filter(variant => variant.normalizedSize);
  };
  const qualifies = variant => variant.available === true && variant.matched !== false;
  const matchesProduct = (item, size) => !normalize(size) || variantsFor(item).some(variant => qualifies(variant) && variant.normalizedSize === normalize(size));
  const availableSizes = items => {
    const found = new Map();
    for (const item of items || []) for (const variant of variantsFor(item)) {
      if (qualifies(variant) && !found.has(variant.normalizedSize)) found.set(variant.normalizedSize, labelFor(variant.size));
    }
    return [...found].map(([value, label]) => ({ value, label })).sort((a, b) => {
      const ai = ORDER.indexOf(a.value), bi = ORDER.indexOf(b.value);
      return (ai < 0 ? ORDER.length : ai) - (bi < 0 ? ORDER.length : bi) || a.label.localeCompare(b.label, undefined, { numeric: true });
    });
  };
  const core = { normalize, labelFor, variantsFor, matchesProduct, availableSizes };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || window.FSMySize) return;

  let chosen = '';
  try { chosen = normalize(window.sessionStorage.getItem(KEY) || '').slice(0, 80); } catch (_) {}
  const defaults = {
    heading: 'My size', group_label: 'Filter products by my size', reset: 'Reset', view_all: 'View all products',
    choose: 'Choose your size to filter products. Your choice is remembered for this visit.',
    results: 'Available to order in [size]: [count] in [scope].', collection_scope: 'this collection', loaded_scope: 'currently loaded products',
    empty_complete: 'No products available to order in [size] in this collection. Reset your size or view all.',
    empty_partial: 'No products available to order in [size] among currently loaded products. Reset your size, load more products or view all.',
    unknown: 'Size details are unavailable for [count] products.', all_sizes: 'All sizes', reset_label: 'Reset my size and show all products',
    remembered: 'Filters products available to order. Remembered for this visit.',
    alternatives_complete: 'Other products available to order in [size]', alternatives_partial: 'Currently loaded products available to order in [size]'
  };
  let stringSource = '', strings = defaults;
  const text = (key, values = {}) => {
    const source = document.querySelector('script[data-fs-my-size-strings]')?.textContent || '';
    if (source && source !== stringSource) {
      stringSource = source;
      try { strings = { ...defaults, ...JSON.parse(source) }; } catch (_) { strings = defaults; }
    }
    return String(strings[key] || defaults[key] || '').replace(/\[(\w+)\]/g, (_, name) => values[name] ?? '');
  };
  const catalogs = new Map();
  const inlineStates = new Map();
  const get = () => chosen;
  const set = (value, options = {}) => {
    const size = normalize(value).slice(0, 80);
    const changed = chosen !== size;
    chosen = size;
    try { if (size) window.sessionStorage.setItem(KEY, size); else window.sessionStorage.removeItem(KEY); } catch (_) {}
    if (changed) document.dispatchEvent(new CustomEvent('fs:my-size-change', { detail: { size, source: options.source || null } }));
    return size;
  };
  const safeURL = (value, previewThemeId = '') => {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/\/products\//.test(url.pathname)) return '';
      url.searchParams.delete('variant');
      if (/^\d+$/.test(previewThemeId)) url.searchParams.set('preview_theme_id', previewThemeId);
      return url.href;
    } catch (_) { return ''; }
  };
  const productFromCard = card => {
    const id = card.dataset.fhgCard || card.querySelector('[data-fs-live-quickview]')?.dataset.fsLiveQuickview || card.querySelector('[data-fs-collection-add]')?.dataset.fsCollectionAdd;
    let product = null, sizeMeta = null;
    try { product = JSON.parse(card.querySelector('script[data-fs-collection-product]')?.textContent || 'null')?.product || null; } catch (_) {}
    try { sizeMeta = JSON.parse(card.querySelector('[data-fs-live-size-meta]')?.dataset.fsLiveSizeMeta || 'null'); } catch (_) {}
    const link = [...card.querySelectorAll('a[href]')].find(node => safeURL(node.getAttribute('href')));
    return { id: String(sizeMeta?.id || product?.id || id || ''), title: product?.title || card.querySelector('h3, .card__heading')?.textContent?.trim() || '', url: link?.href || product?.url || '', image: product?.image || card.querySelector('img')?.getAttribute('src') || '', product, sizeMeta };
  };
  const makeButton = (size, className, attribute) => {
    const button = document.createElement('button');
    button.type = 'button'; button.className = className; button.setAttribute(attribute, size.value);
    button.textContent = size.label; button.setAttribute('aria-pressed', String(size.value === chosen));
    return button;
  };
  const buildInlineUI = root => {
    let control = root.querySelector('[data-fs-my-size]');
    if (!control) return null; // The Liquid snippet owns placement; never create an invisible second filter.
    const row = control.querySelector('[data-fs-my-size-options]');
    const status = control.querySelector('[data-fs-my-size-status]');
    if (!row || !status) return null;
    return { control, row, status, reset: control.querySelector('[data-fs-my-size-reset]'), viewAll: control.querySelector('[data-fs-my-size-view-all]') };
  };
  const renderInline = state => {
    const { root, ui, items } = state;
    if (!root.isConnected) { inlineStates.delete(root); catalogs.delete(root); return; }
    const sizes = availableSizes(items);
    const chosenLabel = sizes.find(size => size.value === chosen)?.label || labelFor(chosen);
    const activeElement = document.activeElement;
    const focusSize = ui.row.contains(activeElement) ? activeElement?.getAttribute('data-fs-my-size-value') : null;
    ui.row.replaceChildren(...sizes.map(size => makeButton(size, 'fs-my-size__option', 'data-fs-my-size-value')));
    if (focusSize != null) [...ui.row.querySelectorAll('button')].find(button => button.dataset.fsMySizeValue === focusSize)?.focus({ preventScroll: true });
    ui.reset.hidden = !chosen;
    const cards = [...root.querySelectorAll('[data-fsic-grid] > [data-fhg-card]')];
    const byId = new Map(items.map(item => [String(item.id || dataFor(item)?.id), item]));
    let matching = 0, unknown = 0;
    for (const card of cards) {
      const item = byId.get(String(card.dataset.fhgCard));
      const data = dataFor(item);
      const known = data && data.complete !== false && (data.hasSize === false || variantsFor(item).length > 0 || (Array.isArray(data.options) && Array.isArray(data.variants) && !data.options.some(option => /(^|\b)sizes?(\b|$)/i.test(typeof option === 'string' ? option : option?.name || ''))));
      if (!known) unknown += 1;
      const hide = Boolean(chosen && !matchesProduct(item, chosen));
      if (hide) {
        if (!state.hidden.has(card)) state.hidden.set(card, card.hidden);
        card.hidden = true; card.dataset.fsMySizeHidden = 'true';
      } else if (state.hidden.has(card)) {
        card.hidden = state.hidden.get(card); state.hidden.delete(card); delete card.dataset.fsMySizeHidden;
      }
      if (!hide) matching += 1;
    }
    const complete = state.complete && unknown === 0;
    const scope = text(complete ? 'collection_scope' : 'loaded_scope');
    ui.control.hidden = !sizes.length && !chosen;
    if (ui.viewAll) {
      ui.viewAll.hidden = !chosen;
      if (state.viewAllURL) ui.viewAll.href = state.viewAllURL;
    }
    ui.control.classList.toggle('fs-my-size--empty', Boolean(chosen && !matching));
    if (!chosen) ui.status.textContent = sizes.length ? text('choose') : '';
    else if (matching) ui.status.textContent = text('results', { count: matching, size: chosenLabel, scope });
    else ui.status.textContent = text(complete ? 'empty_complete' : 'empty_partial', { size: chosenLabel });
    if (chosen && unknown) ui.status.textContent += ' ' + text('unknown', { count: unknown });
    catalogs.set(root, { items, complete: state.complete && unknown === 0, previewThemeId: root.dataset.previewThemeId || '' });
    root.dispatchEvent(new CustomEvent('fs:my-size:applied', { bubbles: true, detail: { size: chosen, count: matching, complete: state.complete && unknown === 0 } }));
  };
  const registerInline = (root, details = {}) => {
    if (!root) return;
    let state = inlineStates.get(root);
    if (!state) {
      const ui = buildInlineUI(root); if (!ui) return;
      state = { root, ui, items: [], complete: false, hidden: new Map() };
      inlineStates.set(root, state);
      ui.control.addEventListener('click', event => {
        if (event.target.closest('[data-fs-my-size-view-all]') && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) { set('', { source: root }); return; }
        const button = event.target.closest('[data-fs-my-size-value], [data-fs-my-size-reset]');
        if (!button || !ui.control.contains(button)) return;
        event.preventDefault();
        set(button.hasAttribute('data-fs-my-size-reset') ? '' : button.dataset.fsMySizeValue, { source: root });
      });
    }
    const cards = [...root.querySelectorAll('[data-fsic-grid] > [data-fhg-card]')];
    for (const card of state.hidden.keys()) if (!cards.includes(card)) state.hidden.delete(card);
    const byId = new Map(cards.map(card => productFromCard(card)).filter(item => item.id).map(item => [String(item.id), item]));
    for (const item of details.items || []) {
      const id = String(item.id || item.product?.id || item.sizeMeta?.id || '');
      if (id && byId.has(id)) byId.set(id, { ...byId.get(id), ...item });
    }
    state.items = [...byId.values()];
    const total = Number(details.total ?? root.fsic?.total);
    state.complete = details.complete === true && Number.isSafeInteger(total) && total === cards.length;
    state.viewAllURL = details.viewAllURL || root.querySelector('[data-fsic-view-all]')?.href || '';
    renderInline(state);
  };
  const recordCollection = (controller, records, complete = false) => {
    const items = records.map(({ card, meta }) => ({ ...productFromCard(card), id: String(meta.id), sizeMeta: meta }));
    catalogs.set(controller, { items, complete: complete === true, previewThemeId: controller.dataset.previewThemeId || '' });
  };
  const collectionSizes = (controller, records) => {
    const row = controller.querySelector('.fs-live-size-filter__row');
    if (!row) return;
    recordCollection(controller, records, false);
    row.querySelectorAll('[data-size]:not([data-size=""])').forEach(button => button.remove());
    availableSizes(records.map(record => record.meta)).forEach(size => row.append(makeButton(size, 'fs-live-size-filter__button', 'data-size')));
  };
  const collectionReady = controller => {
    controller.setAttribute('data-fs-my-size-collection', '');
    const heading = controller.querySelector('.fs-live-size-filter__heading');
    if (heading) heading.textContent = text('heading');
    const group = controller.querySelector('.fs-live-size-filter__row');
    group?.setAttribute('aria-label', text('group_label'));
    const all = group?.querySelector('[data-size=""]');
    if (all) all.textContent = text('all_sizes');
    const clear = controller.querySelector('[data-fs-live-size-clear]');
    if (clear) clear.setAttribute('aria-label', text('reset_label'));
    let hint = controller.querySelector('[data-fs-my-size-hint]');
    if (!hint) {
      hint = document.createElement('p'); hint.className = 'fs-my-size__hint'; hint.setAttribute('data-fs-my-size-hint', '');
      hint.textContent = text('remembered');
      group?.after(hint);
    }
    if (!controller.fsMySizeListener) {
      controller.fsMySizeListener = event => {
        if (event.detail.source !== controller && controller.isConnected && controller.selected !== event.detail.size) controller.apply(event.detail.size);
      };
      document.addEventListener('fs:my-size-change', controller.fsMySizeListener);
    }
  };
  const collectionDisconnected = controller => {
    if (controller.fsMySizeListener) document.removeEventListener('fs:my-size-change', controller.fsMySizeListener);
    controller.fsMySizeListener = null; catalogs.delete(controller);
  };
  const alternatives = (owner, excludedId, requestedSize = chosen) => {
    const size = normalize(requestedSize);
    const inline = owner?.closest?.('[data-fs-inline-categories]') || owner?.querySelector?.('[data-fs-inline-categories]');
    const collection = owner?.closest?.('fs-live-size-filter') || owner?.querySelector?.('fs-live-size-filter') || owner?.closest?.('[data-fs-live-collection]')?.querySelector('fs-live-size-filter');
    const catalog = catalogs.get(inline) || catalogs.get(collection);
    const complete = catalog?.complete === true;
    const sizeLabel = availableSizes(catalog?.items || []).find(item => item.value === size)?.label || labelFor(size);
    const items = !size || !catalog ? [] : catalog.items.filter(item => String(item.id) !== String(excludedId) && matchesProduct(item, size)).map(item => ({ id: String(item.id), title: item.title, url: safeURL(item.url, catalog.previewThemeId), image: item.image })).filter(item => item.url && item.title);
    return { items, complete, size, label: text(complete ? 'alternatives_complete' : 'alternatives_partial', { size: sizeLabel }) };
  };
  document.addEventListener('fs:my-size-change', () => inlineStates.forEach(renderInline));
  document.addEventListener('fs:catalog-change', event => {
    const root = event.target.closest?.('[data-fs-inline-categories]');
    if (root) registerInline(root, event.detail || {});
  });
  const boot = scope => scope.querySelectorAll('[data-fs-inline-categories]').forEach(root => registerInline(root));
  window.FSMySize = { ...core, get, set, reset: () => set(''), registerInline, recordCollection, collectionSizes, collectionReady, collectionDisconnected, alternatives };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => queueMicrotask(() => boot(document)), { once: true });
  else queueMicrotask(() => boot(document));
  document.addEventListener('shopify:section:load', event => queueMicrotask(() => boot(event.target)));
  document.addEventListener('shopify:section:unload', event => {
    inlineStates.forEach((state, root) => { if (event.target.contains(root)) { inlineStates.delete(root); catalogs.delete(root); } });
  });
})();
